import { useEffect, useState } from 'react';
import { CheckCircle2, Route, XCircle } from 'lucide-react';
import { useSandboxRunJourney } from '@/services/mutations/sandbox';
import {
	SandboxField,
	SandboxModal,
	SandboxNotice,
	sandboxSelectClass,
} from './SandboxModal';
import { JOURNEYS, formatMoney, fullName } from '../sandboxLabels';
import { cn } from '@/lib/utils';

interface Props {
	scenarioId: string;
	catalog: Sandbox.Catalog;
	professionals: Sandbox.ScenarioProfessional[];
	isOpen: boolean;
	onClose: () => void;
}

/** Roda uma jornada completa e mostra o resultado passo a passo. */
const SandboxJourneyModal = ({
	scenarioId,
	catalog,
	professionals,
	isOpen,
	onClose,
}: Props) => {
	const [journey, setJourney] = useState<Sandbox.Journey>('B2C_SUBSCRIPTION');
	const [clubId, setClubId] = useState('');
	const [serviceId, setServiceId] = useState('');
	const [professionalId, setProfessionalId] = useState('');
	const [result, setResult] = useState<Sandbox.JourneyResult | null>(null);

	useEffect(() => {
		if (!isOpen) return;
		setJourney('B2C_SUBSCRIPTION');
		setClubId('');
		setServiceId('');
		setProfessionalId('');
		setResult(null);
	}, [isOpen]);

	const { mutate, isPending } = useSandboxRunJourney(scenarioId, setResult);

	const b2cClubs = catalog.clubs.filter((c) => c.shelf === 'B2C');
	const authorizedProfessionals = professionals.filter((p) => p.authorized);

	return (
		<SandboxModal
			isOpen={isOpen}
			onClose={onClose}
			title="Jornada guiada"
			description="Executa vários passos em sequência pelas regras reais. Se um passo falhar, a jornada para e o que já foi criado fica no cenário."
			icon={<Route className="h-4 w-4" />}
			confirmText={result ? undefined : 'Rodar jornada'}
			onConfirm={
				result
					? undefined
					: () =>
							mutate({
								journey,
								club_id: clubId || undefined,
								service_id: serviceId || undefined,
								professional_id: professionalId || undefined,
							})
			}
			isPending={isPending}
			size="wide"
		>
			<div className="space-y-4 py-2">
				{result ? (
					<div className="space-y-3">
						<SandboxNotice
							tone={result.completed ? 'success' : 'warning'}
						>
							{result.completed
								? 'Jornada concluída. Veja as entidades e a linha do tempo do cenário.'
								: 'Jornada interrompida. O passo com erro mostra o motivo (regra real que barrou).'}
						</SandboxNotice>
						<ol className="space-y-2">
							{result.steps.map((step, index) => (
								<li
									key={`${step.step}-${index}`}
									className={cn(
										'flex items-start gap-3 rounded-xl border p-3',
										step.ok
											? 'border-emerald-100 bg-emerald-50/40'
											: 'border-rose-100 bg-rose-50/40',
									)}
								>
									{step.ok ? (
										<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
									) : (
										<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
									)}
									<div className="min-w-0">
										<p className="text-sm font-bold text-stone-900">
											{index + 1}. {step.step}
										</p>
										<p className="break-words text-xs text-stone-500">
											{step.message}
										</p>
									</div>
								</li>
							))}
						</ol>
					</div>
				) : (
					<>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							{JOURNEYS.map((option) => (
								<button
									key={option.key}
									type="button"
									onClick={() => setJourney(option.key)}
									className={cn(
										'rounded-2xl border p-4 text-left transition-colors',
										journey === option.key
											? 'border-stone-900 bg-stone-50'
											: 'border-stone-200 hover:border-stone-400',
									)}
								>
									<p className="text-sm font-black text-stone-900">
										{option.label}
									</p>
									<p className="mt-1 text-xs leading-relaxed text-stone-500">
										{option.description}
									</p>
								</button>
							))}
						</div>

						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							<SandboxField label="Serviço">
								<select
									className={sandboxSelectClass}
									value={serviceId}
									onChange={(e) =>
										setServiceId(e.target.value)
									}
								>
									<option value="">
										automático (psicologia)
									</option>
									{catalog.services.map((s) => (
										<option key={s.id} value={s.id}>
											{s.service_name}
										</option>
									))}
								</select>
							</SandboxField>
							<SandboxField label="Profissional">
								<select
									className={sandboxSelectClass}
									value={professionalId}
									onChange={(e) =>
										setProfessionalId(e.target.value)
									}
								>
									<option value="">criar um novo</option>
									{authorizedProfessionals.map((p) => (
										<option key={p.id} value={p.id}>
											{fullName(p)}
										</option>
									))}
								</select>
							</SandboxField>
							{journey === 'B2C_SUBSCRIPTION' ? (
								<SandboxField label="Clube">
									<select
										className={sandboxSelectClass}
										value={clubId}
										onChange={(e) =>
											setClubId(e.target.value)
										}
									>
										<option value="">
											automático (1º B2C com benefício)
										</option>
										{b2cClubs.map((c) => (
											<option key={c.id} value={c.id}>
												{c.name} ·{' '}
												{formatMoney(c.price)}
											</option>
										))}
									</select>
								</SandboxField>
							) : null}
						</div>

						{journey === 'B2C_SUBSCRIPTION' &&
						!catalog.match_form ? (
							<SandboxNotice tone="warning">
								Sem formulário de match ativo: o passo do match
								vai falhar, mas a jornada segue.
							</SandboxNotice>
						) : null}
					</>
				)}
			</div>
		</SandboxModal>
	);
};

export { SandboxJourneyModal };
